"use client"

import styled from "@emotion/styled"
import { Pagination, PaginationItem } from "@mui/material"
import Link from "next/link"
import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, useState } from "react"
import { Container, Desktop, Mobile, NewText, TopBanner } from "ui"
import { extractImage, extractProductsAndFilters, extractProperty } from "utils"
import Filters from "./Filters"
import { filterProducts } from "./FiltersUtils/filterProducts"
import ProductCard from "./ProductCard"

const StyledContainer = styled.div`
  padding-top: 16px;
  ${(p) => p.theme.breakpoints.up("md")} {
    padding-top: 88px;
  }
`
const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  padding: 0 16px;
  gap: 24px;
  ${(p) => p.theme.breakpoints.up("md")} {
    gap: 40px 24px;
    grid-template-columns: repeat(auto-fit, minmax(260px, 328px));
  }
  ${(p) => p.theme.breakpoints.up("lg")} {
    grid-template-columns: repeat(4, minmax(260px, 328px));
  }
`

const StyledTitle = styled(NewText)`
  padding: 24px 0;
  ${(p) => p.theme.breakpoints.up("md")} {
    padding: 40px 0;
  }
`

const StyledPaginationDiv = styled.div`
  margin-top: 32px;
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: 40px;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  li {
    button {
      font-size: 16px;
      border: 1px solid #f7f7f7;
      &:last-child {
        border: none;
      }
      &:first-child {
        border: none;
      }
    }
    .Mui-selected {
      background-color: ${(props) => props.theme.colors.paginationBackgroundColor};
      color: ${(props) => props.theme.colors.paginationTextColor};
      ${(props) => props.theme.breakpoints.down("md")} {
        &:hover {
          background-color: ${(props) => props.theme.colors.paginationBackgroundColor};
          color: ${(props) => props.theme.colors.paginationTextColor};
        }
      }
    }
  }
`

const StyledResponse = styled.div`
  font-size: 18px;
  padding-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 40px;
    padding-top: 40px;
  }
`

export type FilterOptionState = {
  number: number
  visibility: boolean
  value: string
  id: number
}

export type StateType = {
  categories: FilterOptionState[]
  subCategories: FilterOptionState[]
  productKeyFeatures: FilterOptionState[]
}

const defaultFilterKeys = ["categories", "subCategories", "productKeyFeatures"]

export function restoreObjectFromSearchParams(
  queryString: string,
  initialData: StateType,
): StateType {
  const params = new URLSearchParams(queryString)
  const restoredObject: StateType = { ...initialData }

  params.forEach((value, key) => {
    if (restoredObject[key as keyof StateType]) {
      restoredObject[key as keyof StateType] = restoredObject[key as keyof StateType].map(
        (item) => ({
          ...item,
          // Set visibility to true if the value matches; otherwise retain the original visibility
          visibility: item.value === value ? true : item.visibility,
        }),
      )
    }
  })

  return restoredObject
}

export type ProductFilter = {
  label: string
  options: {
    id: number
    name: string
    value: string
  }[]
}
export type ProductFilters = {
  categories: ProductFilter
  subCategories: ProductFilter
  productKeyFeatures: ProductFilter
}

export function initializeFilterOptions<T extends keyof ProductFilters>(
  data: ProductFilters[T],
): FilterOptionState[] {
  const { options } = data
  return options.map((option) => ({
    number: option.id,
    visibility: false,
    value: option.value,
    id: option.id,
  }))
}

export function initialState(filters: ProductFilters) {
  return {
    categories: initializeFilterOptions(filters.categories),
    subCategories: initializeFilterOptions(filters.subCategories),
    productKeyFeatures: initializeFilterOptions(filters.productKeyFeatures),
  }
}

const allHebrew = "כל+המוצרים"
const allEnglish = "All+Products"

function checkFiltered(query: string) {
  const includesKeywords = query.includes("subCategories") || query.includes("productKeyFeatures")

  const includesCategories =
    query.includes("categories") &&
    !query.includes(encodeURIComponent(allHebrew)) &&
    !query.includes(encodeURIComponent(allEnglish))

  if (includesKeywords || includesCategories) {
    return true
  } else {
    return false
  }
}

const productsPerPage = 9

const getProductsPerPage = (page: number, products: any) => {
  const startIndex = (page - 1) * productsPerPage
  const endIndex = startIndex + productsPerPage
  const productsForPage = products.slice(startIndex, endIndex)
  return productsForPage
}

function ProductsLobby({
  locale,
  pageData,
  messages,
  page,
  productCategories,
}: {
  locale: "he" | "en"
  pageData: any
  messages?: any
  page: string
  searchText?: string
  productsForPage?: any
  productCategories?: any
}) {
  const { products, filters } = extractProductsAndFilters({
    products: pageData?.children || [],
    filters: productCategories,
  })
  const searchParams = useSearchParams()
  const queryString = searchParams.toString()
  const stateWithParams = restoreObjectFromSearchParams(queryString, initialState(filters))
  const filteredPage = Number(searchParams.get("page") || 1)
  const [pageNum, setPageNum] = useState(filteredPage)
  const [state, setState] = useState<StateType>(stateWithParams)
  const [interimState, setInterimState] = useState<StateType>(state)
  const [startIndex, setStartIndex] = useState(0)
  const [endIndex, setEndIndex] = useState(9)

  const [isFilterApplied, setIsFilterApplied] = useState(
    queryString && checkFiltered(queryString) ? true : false,
  )

  const pathname = usePathname()
  const productsForPage = getProductsPerPage(pageNum, products)
  const filteredProducts = isFilterApplied
    ? filterProducts(products, state)?.filter(() => true)
    : filterProducts(productsForPage, state)?.filter(() => true)

  const totalPages = Math.ceil(
    isFilterApplied ? filteredProducts.length / productsPerPage : products.length / productsPerPage,
  )
  const slicedProducts = filteredProducts?.slice(
    startIndex,
    endIndex > filteredProducts.length ? filteredProducts.length : endIndex,
  )
  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPageNum(value)
    window.scrollTo({ top: 0, behavior: "smooth" }) // Scroll to top smoothly

    if (isFilterApplied) {
      const startIndex = (value - 1) * productsPerPage
      const endIndex = startIndex + productsPerPage
      setEndIndex(endIndex)
      setStartIndex(startIndex)
    }
  }

  const allDesign = locale === "en" ? allEnglish : allHebrew
  const setIfUnfilteredParams = () => {
    const newSearchParams = new URLSearchParams()
    const visibleCategories = state.categories.filter((cat) => cat.visibility)

    // Check if 'categories' is present in the current URL
    const hasCategoriesParam = searchParams.has("categories")

    if (!hasCategoriesParam) {
      // If no 'categories' in URL, default to allDesign
      newSearchParams.set("categories", allDesign)
    } else {
      if (visibleCategories.length > 0) {
        // If categories are visible in state, append each visible one
        visibleCategories.forEach((cat) => {
          newSearchParams.append("categories", cat.value)
        })

        // If existing 'categories' param was allDesign, remove and replace
        const existingCategories = searchParams.getAll("categories")
        if (
          existingCategories.length === 1 &&
          decodeURIComponent(existingCategories[0]) === decodeURIComponent(allDesign)
        ) {
          newSearchParams.delete("categories")
          visibleCategories.forEach((cat) => {
            newSearchParams.append("categories", cat.value)
          })
          setIsFilterApplied(true)
        }
      } else {
        // If no visible categories in state, default to allDesign
        newSearchParams.set("categories", allDesign)
      }
    }

    // Add pagination
    if (pageNum) {
      newSearchParams.set("page", pageNum.toString())
    }

    // Preserve other params (not in defaultFilterKeys)
    searchParams.forEach((value, key) => {
      if (!defaultFilterKeys.includes(key) && !newSearchParams.has(key)) {
        newSearchParams.set(key, value)
      }
    })

    const newQueryString = newSearchParams.toString()

    try {
      window.history.pushState(
        { categories: newSearchParams.getAll("categories"), page: pageNum },
        "",
        `${pathname}?${newQueryString}`,
      )
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }
  }

  const handleUnfilteredChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPageNum(value)
    window.scrollTo({ top: 0, behavior: "smooth" }) // Scroll to top smoothly
  }

  useEffect(() => {
    if (isFilterApplied) {
      const newSearchParams = new URLSearchParams()

      const visibleCategories = state.categories.filter((cat) => cat.visibility)
      const allCategoriesVisible = visibleCategories.length === state.categories.length

      // Always remove existing categories from params
      newSearchParams.delete("categories")

      if (allCategoriesVisible || visibleCategories.length === 0) {
        newSearchParams.delete("categories")
      } else {
        visibleCategories.forEach((cat) => {
          newSearchParams.append("categories", cat.value)
        })
      }

      // Add other visible filters (subCategories, productKeyFeatures)
      Object.keys(state).forEach((key) => {
        if (key === "categories") return
        state[key as keyof StateType].forEach((item) => {
          if (item.visibility) {
            newSearchParams.append(key, item.value)
          }
        })
      })

      // Page param logic
      if (pageNum > 1 && totalPages >= pageNum) {
        newSearchParams.set("page", pageNum.toString())
      } else {
        setPageNum(1)
        newSearchParams.set("page", "1")
      }

      // Preserve unrelated query params
      searchParams.forEach((value, key) => {
        if (!defaultFilterKeys.includes(key) && !newSearchParams.has(key)) {
          newSearchParams.set(key, value)
        }
      })

      const newQueryString = newSearchParams.toString()
      const activeCategories = visibleCategories
        ? visibleCategories?.map((cat) => {
            return cat.value
          })
        : allDesign
      try {
        window.history.pushState(
          { categories: activeCategories, page: pageNum },
          "",
          `${pathname}?${newQueryString}`,
        )
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e)
      }

      if (allCategoriesVisible || visibleCategories.length === 0) {
        setIsFilterApplied(false)
      }
    } else {
      setIfUnfilteredParams()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, pageNum, pathname, isFilterApplied, totalPages, queryString, searchParams, locale])

  const topBanner = extractProperty(pageData, "mainBanner")?.[0]
  const mobileImage = extractProperty(topBanner, "mobileImage")
  const videoMobile = extractProperty(topBanner, "videoMobile")
  const desktopImage = extractImage(topBanner, "desktopImage")?.url
  const videoOptional = extractProperty(topBanner, "videoOptional")
  const pageTitle = extractProperty(pageData, "pageTitle")

  return (
    <StyledContainer>
      {(mobileImage || videoMobile) && (
        <Mobile>
          <TopBanner data={topBanner} videoHeight={"340px"} imageHeight={"340px"} />
        </Mobile>
      )}
      <Container>
        {(desktopImage || videoOptional) && (
          <Desktop>
            <TopBanner
              data={topBanner}
              videoHeight={"240px"}
              imageHeight={"240px"}
              borderRadius="16px"
            />
          </Desktop>
        )}
        <StyledTitle
          desktopFontSize={48}
          desktopFontWeight="600"
          desktopLineHeight="100%"
          mobileFontSize={40}
          mobileFontWeight="600"
          mobileLineHeight={"46px"}
          component="h1"
          color={"black"}
          textAlign={"center"}
        >
          {pageTitle}
        </StyledTitle>
      </Container>
      <Container>
        <Filters
          interimState={interimState}
          setInterimState={setInterimState}
          state={state}
          setState={setState}
          filters={filters}
          element="הפרוייקטים שלנו"
          locale={locale}
          setIsFilterApplied={setIsFilterApplied}
          page={page}
          products={products}
          messages={messages}
        />
      </Container>
      {filteredProducts?.length > 0 ? (
        <Container forSwiper>
          <StyledGrid>
            {(isFilterApplied ? slicedProducts : filteredProducts)?.map(
              (product: any, index: number) => {
                const url = product?.url?.split("pages")?.[1]
                const properties = product?.properties
                const name = properties?.find(
                  (property: any) => property.propertyName === "productName",
                )?.propertyValue?.value
                const productCategoryIds: number[] = properties
                  ?.find((property: any) => property.propertyName === "productCategory")
                  ?.propertyValue?.contentList?.map((cat: any) => cat.id)
                const productCategoryNames: string[] =
                  productCategoryIds
                    ?.map((cat) => {
                      return filters.categories?.options?.find((el: any) => el.id === cat)?.value
                    })
                    ?.filter((el): el is string => typeof el === "string") || []

                const productSubCategoryIds: number[] = properties
                  ?.find((property: any) => property.propertyName === "productSubCategory")
                  ?.propertyValue?.contentList?.map((cat: any) => cat.id)
                const productSubCategoryNames: string[] =
                  productSubCategoryIds
                    ?.map((cat) => {
                      return filters.subCategories?.options?.find((el: any) => el.id === cat)?.value
                    })
                    ?.filter((el): el is string => typeof el === "string") || []

                const image = properties?.find((property: any) => property.propertyName === "image")
                  ?.propertyValue?.mediaItems?.[0]?.url
                return (
                  <ProductCard
                    key={index}
                    productTitle={name}
                    imageURL={image}
                    productURL={url}
                    productSubtitle={productSubCategoryNames}
                    badge={productCategoryNames}
                    locale={locale}
                    sizeLarge={true}
                  />
                )
              },
            )}
          </StyledGrid>
          {isFilterApplied ? (
            <StyledPaginationDiv>
              <Pagination
                count={totalPages}
                color="primary"
                defaultPage={1}
                page={pageNum}
                onChange={handleChange}
                renderItem={(item) => (
                  <PaginationItem
                    component={Link}
                    href={`${pathname}?page=${item.page}`}
                    {...item}
                  />
                )}
              />
            </StyledPaginationDiv>
          ) : (
            <StyledPaginationDiv>
              <Pagination
                count={totalPages}
                color="primary"
                defaultPage={1}
                onChange={handleUnfilteredChange}
                page={pageNum}
                renderItem={(item) => (
                  <PaginationItem
                    component={Link}
                    href={`${pathname}?page=${item.page}`}
                    {...item}
                  />
                )}
              />
            </StyledPaginationDiv>
          )}
        </Container>
      ) : (
        <Container>
          <StyledResponse>{messages.noProductsFound}</StyledResponse>
        </Container>
      )}
    </StyledContainer>
  )
}
export default ProductsLobby
